// Verdict view-model (VB-F1 + VB-F2). Turns the Jarts analysis result envelope
// into everything the verdict screen renders: an overall deterministic verdict
// (severity + one-liner), the AI Trust Signal exposure summary, and one card per
// question grouped by Big-5 - each with a cross-model summary, the surfaced
// signals, and per-model receipts (verbatim + top sources + competitor mentions).
//
// Two grounding rules the acceptance criteria call out:
//  - "your site wasn't cited" is DERIVED from citations (sourceType==="You"),
//    never hardcoded.
//  - the per-question summary prefers the LLM commentary (questionCommentary),
//    and falls back to a deterministic sentence so the screen works before /
//    without the backend commentary pass.

import type {
  AnalysisResultEnvelope,
  Big5Category,
  ResultCitation,
  ResultRunQuestion,
  ResultSubject,
} from "@/types/vb";
import { TRUST_SIGNALS_TOTAL } from "@/types/vb";
import { BIG5_LABELS } from "@/components/vb";
import { providerMeta, KNOWN_PROVIDERS } from "../run/providerMeta";
import { deriveMetricsFromResult } from "../detail/analysisMetrics";
import { BIG5_DISPLAY_ORDER } from "../detail/big5Coverage";
import {
  SIGNAL_GROUP_ORDER,
  SIGNAL_KEYS_BY_GROUP,
  signalName,
  type SignalGroup,
} from "./signalMeta";

export type Severity = "strong" | "mid" | "weak";

export interface SurfacedSignal {
  key: number;
  name: string;
}

export interface ReceiptCitation {
  url: string;
  domain: string;
  path: string;
  /** Short uppercase label (competitor brand, or the URL-classifier category). */
  kind: string;
  /** Dot color class suffix: competitor | owned | influenced | earned. */
  orbit: "competitor" | "owned" | "influenced" | "earned";
}

export interface CompetitorMention {
  name: string;
  count: number;
}

export interface ProviderReceipt {
  providerKey: string;
  label: string;
  /** --vb-model-* class suffix (openai/gemini/perplexity/unknown). */
  className: string;
  model: string;
  answerText: string;
  /** Citations for the full-response dialog (all of them, in answer order). */
  citations: ReceiptCitation[];
  /** Top few citations shown inline on the receipt tile. */
  topCitations: ReceiptCitation[];
  competitorMentions: CompetitorMention[];
}

export interface QuestionVerdict {
  id: string;
  text: string;
  category: Big5Category;
  severity: Severity;
  summary: string;
  /** True when the summary came from the LLM commentary pass (vs derived). */
  summaryFromLlm: boolean;
  surfacedSignals: SurfacedSignal[];
  /** No provider cited the focus company's own site for this question. */
  notCited: boolean;
  /** Total citations across all models (for the receipts toggle copy). */
  receiptCount: number;
  providers: ProviderReceipt[];
}

export interface ExposureGroup {
  group: SignalGroup;
  surfaced: SurfacedSignal[];
  absentCount: number;
}

/** One row of the focus-vs-competitors signal-exposure comparison. */
export interface CompanyExposure {
  name: string;
  url: string; // for the favicon
  signalsExposed: number; // 0..18
  /** Share of judged AI answers this company appeared in (0..1), same metric as
   *  the headline visibility. Null when nothing was judged. */
  visibility: number | null;
  isFocus: boolean;
  /** AI discussed this company substantively (it was a judged subject). */
  appeared: boolean;
}

export interface VerdictModel {
  companyName: string;
  overall: {
    severity: Severity;
    headline: string;
    detail: string;
    signalsExposed: number; // 0..18
    visibility: number | null; // 0..1
    sov: number | null; // 0..1
  };
  exposure: {
    surfacedCount: number;
    absentCount: number;
    total: number; // TRUST_SIGNALS_TOTAL (19)
    groups: ExposureGroup[];
  };
  /** Focus company + the buyer's roster competitors, by signal exposure. Focus
   *  first; only the chosen competitors (not every named company) are compared. */
  competitorExposure: CompanyExposure[];
  /** Categories that have at least one question, in fixed display order. */
  categories: { category: Big5Category; label: string; count: number }[];
  questions: QuestionVerdict[];
}

const TOP_CITATIONS = 3;
/** Leaf signals the judge evaluates (display headline is /19). Mirrors
 *  analysisMetrics.ts so the exposure count is clamped consistently. */
const TRUST_SIGNALS_JUDGED = 18;

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function focusSubject(q: ResultRunQuestion): ResultSubject | undefined {
  return q.judgment?.subjects?.find((s) => s?.isFocus);
}

function siteCited(citations: ResultCitation[] | null | undefined): boolean {
  return !!citations?.some((c) => (c?.sourceType || "").toLowerCase() === "you");
}

/** Resolve the focus company display name: prefer the roster's customer brand,
 *  fall back to the passed-in buyer company name. */
function resolveCompanyName(result: AnalysisResultEnvelope, fallback: string): string {
  const customer = (result.brands || []).find((b) => b?.isCustomer && (b.name || "").trim());
  return customer?.name?.trim() || fallback;
}

/** Lower-cased competitor brand name -> display name (roster minus the focus). */
function competitorLookup(result: AnalysisResultEnvelope): Map<string, string> {
  const out = new Map<string, string>();
  for (const b of result.brands || []) {
    if (!b || b.isCustomer) continue;
    const name = (b.name || "").trim();
    if (name) out.set(name.toLowerCase(), name);
  }
  // Competitors that never got mentioned still belong to the roster.
  for (const c of result.competitors || []) {
    const name = (c?.name || "").trim();
    if (name && !out.has(name.toLowerCase())) out.set(name.toLowerCase(), name);
  }
  return out;
}

function citationOrbit(c: ResultCitation): ReceiptCitation["orbit"] {
  if (c.isCompetitorDomain || c.competitorBrand) return "competitor";
  switch ((c.sourceType || "").toLowerCase()) {
    case "you":
      return "owned";
    case "peer":
      return "influenced";
    default:
      return "earned";
  }
}

function citationKind(c: ResultCitation): string {
  if (c.competitorBrand) return c.competitorBrand;
  if (c.isCompetitorDomain) return "Competitor";
  // Trim the verbose classifier bucket ("Review & Comparison" -> "Review").
  const cat = (c.category || "").split("&")[0].trim();
  return cat || "Source";
}

function splitUrl(c: ResultCitation): { domain: string; path: string } {
  const domain = (c.rootDomain || c.host || "").replace(/^www\./, "");
  let path = "";
  try {
    const u = new URL(c.url || c.displayUrl || "");
    path = u.pathname && u.pathname !== "/" ? u.pathname : "";
  } catch {
    path = "";
  }
  // Keep paths short so the receipt row stays single-line on desktop.
  if (path.length > 28) path = path.slice(0, 27) + "…";
  return { domain, path };
}

function toReceiptCitation(c: ResultCitation): ReceiptCitation {
  const { domain, path } = splitUrl(c);
  return {
    url: c.url || c.displayUrl || "",
    domain,
    path,
    kind: citationKind(c),
    orbit: citationOrbit(c),
  };
}

/** De-dup citations by URL, preserving answer order (Jarts already dedupes per
 *  row, but a defensive pass keeps the dialog clean if shapes drift). */
function dedupeCitations(citations: ResultCitation[] | null | undefined): ResultCitation[] {
  const seen = new Set<string>();
  const out: ResultCitation[] = [];
  for (const c of citations || []) {
    const key = (c?.url || c?.displayUrl || "").toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(c);
  }
  return out;
}

function questionSeverity(
  focusMentions: number,
  providerCount: number,
  signalsSurfaced: number,
): Severity {
  if (providerCount === 0 || focusMentions === 0) return "weak";
  if (focusMentions >= providerCount && signalsSurfaced >= 3) return "strong";
  return "mid";
}

function overallSeverity(visibility: number | null, signalsExposed: number): Severity {
  if (visibility == null) return "weak";
  if (visibility >= 0.8 && signalsExposed >= 6) return "strong";
  if (visibility < 0.34) return "weak";
  return "mid";
}

function pct(x: number | null): string {
  return x == null ? "n/a" : `${Math.round(x * 100)}%`;
}

function deterministicSummary(
  company: string,
  providerCount: number,
  focusMentions: number,
  competitors: CompetitorMention[],
  anySiteCited: boolean,
  surfaced: SurfacedSignal[],
): string {
  const parts: string[] = [];
  if (focusMentions === 0) {
    parts.push(`None of the ${providerCount} models named ${company}.`);
  } else if (focusMentions >= providerCount) {
    parts.push(`All ${providerCount} models named ${company}.`);
  } else {
    parts.push(`${focusMentions} of ${providerCount} models named ${company}.`);
  }
  if (competitors.length) {
    const named = competitors
      .slice(0, 3)
      .map((c) => c.name)
      .join(", ");
    parts.push(
      focusMentions === 0
        ? `They pointed to ${named} instead.`
        : `${named} also came up.`,
    );
  }
  if (!anySiteCited) {
    parts.push(`No model cited ${company}'s site.`);
  } else if (surfaced.length) {
    parts.push(`Signals surfaced: ${surfaced.slice(0, 3).map((s) => s.name).join(", ")}.`);
  }
  return parts.join(" ");
}

export function buildVerdictModel(
  result: AnalysisResultEnvelope | null | undefined,
  companyFallback: string,
): VerdictModel | null {
  if (!result || !Array.isArray(result.runs) || result.runs.length === 0) return null;

  const companyName = resolveCompanyName(result, companyFallback);
  const competitors = competitorLookup(result);
  const commentary = result.questionCommentary || {};

  // Question metadata (text + category) by id, from the run-time snapshot.
  const meta = new Map<string, { text: string; category: Big5Category }>();
  for (const q of result.questions || []) {
    if (q?.id) meta.set(q.id, { text: q.text || "", category: q.category });
  }

  // Group every (run, question) row by questionId, keyed provider order stable.
  const byQid = new Map<string, Map<string, ResultRunQuestion>>();
  for (const run of result.runs) {
    const provider = (run.provider || "").toUpperCase();
    for (const rq of run.questions || []) {
      const qid = rq?.questionId;
      if (!qid) continue;
      if (!byQid.has(qid)) byQid.set(qid, new Map());
      byQid.get(qid)!.set(provider, rq);
    }
  }

  // Stable provider column order: known providers first, then any extras.
  const providerOrder = (qmap: Map<string, ResultRunQuestion>): string[] => {
    const known = (KNOWN_PROVIDERS as readonly string[]).filter((p) => qmap.has(p));
    const extra = [...qmap.keys()].filter((p) => !known.includes(p));
    return [...known, ...extra];
  };

  // lower-cased subject company name -> union of exposed signal keys, for the
  // focus-vs-competitors comparison (resolved to the roster after the loop).
  const competitorSignalKeys = new Map<string, Set<number>>();
  // Per-company appearance share: how many judged answers each company showed
  // up in, over the same denominator (totalJudged) as the headline visibility.
  const competitorAppearances = new Map<string, number>();
  let totalJudged = 0;
  let focusAppearances = 0;

  const questions: QuestionVerdict[] = [];
  for (const [qid, qmap] of byQid) {
    const m = meta.get(qid);
    if (!m) continue; // question edited/removed since the run

    const providers: ProviderReceipt[] = [];
    const surfacedMap = new Map<number, string>();
    const competitorMentionTotals = new Map<string, number>();
    let focusMentions = 0;
    let anySiteCited = false;

    for (const providerKey of providerOrder(qmap)) {
      const rq = qmap.get(providerKey)!;
      const pm = providerMeta(providerKey);
      const subj = focusSubject(rq);
      if (subj) focusMentions += 1;
      if (siteCited(rq.citations)) anySiteCited = true;

      // Surfaced signals (focus, exposed) accumulate across providers (union).
      for (const sig of subj?.signals || []) {
        if (sig?.exposed && sig.key != null) {
          surfacedMap.set(sig.key, signalName(sig.key, sig.name));
        }
      }

      // Per-answer accounting for the focus-vs-competitors comparison: count
      // this as a judged answer (any answer the judge ran, even if it named no
      // company - same denominator as the headline visibility), tally which
      // companies appeared in it (once each), and union each non-focus
      // company's exposed signals.
      const subs = rq.judgment?.subjects;
      if (Array.isArray(subs)) {
        totalJudged += 1;
        let hasFocus = false;
        const seenHere = new Set<string>();
        for (const s of subs) {
          if (!s) continue;
          if (s.isFocus) {
            hasFocus = true;
            continue;
          }
          const key = (s.company || "").trim().toLowerCase();
          if (!key) continue;
          if (!seenHere.has(key)) {
            seenHere.add(key);
            competitorAppearances.set(key, (competitorAppearances.get(key) || 0) + 1);
          }
          let set = competitorSignalKeys.get(key);
          if (!set) {
            set = new Set<number>();
            competitorSignalKeys.set(key, set);
          }
          for (const sig of s.signals || []) {
            if (sig?.exposed && sig.key != null) set.add(sig.key);
          }
        }
        if (hasFocus) focusAppearances += 1;
      }

      // Competitor mentions on this answer (roster-resolved).
      const perProviderCompetitors: CompetitorMention[] = [];
      for (const [brand, count] of Object.entries(rq.mentions || {})) {
        const display = competitors.get(brand.trim().toLowerCase());
        const n = Number(count) || 0;
        if (display && n > 0) {
          perProviderCompetitors.push({ name: display, count: n });
          competitorMentionTotals.set(display, (competitorMentionTotals.get(display) || 0) + n);
        }
      }
      perProviderCompetitors.sort((a, b) => b.count - a.count);

      const cites = dedupeCitations(rq.citations).map(toReceiptCitation);
      providers.push({
        providerKey,
        label: pm.label,
        className: pm.className,
        model: "", // filled from the run row below
        answerText: (rq.answerText || "").trim(),
        citations: cites,
        topCitations: cites.slice(0, TOP_CITATIONS),
        competitorMentions: perProviderCompetitors,
      });
    }

    // Fill model labels from the run rows (provider->model).
    for (const p of providers) {
      const run = result.runs.find((r) => (r.provider || "").toUpperCase() === p.providerKey);
      p.model = run?.model || "";
    }

    const surfacedSignals: SurfacedSignal[] = [...surfacedMap.entries()]
      .map(([key, name]) => ({ key, name }))
      .sort((a, b) => a.key - b.key);

    const competitorList: CompetitorMention[] = [...competitorMentionTotals.entries()]
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    const providerCount = providers.length;
    const severity = questionSeverity(focusMentions, providerCount, surfacedSignals.length);

    const llm = (commentary[qid] || "").trim();
    const summary =
      llm ||
      deterministicSummary(
        companyName,
        providerCount,
        focusMentions,
        competitorList,
        anySiteCited,
        surfacedSignals,
      );

    questions.push({
      id: qid,
      text: m.text,
      category: m.category,
      severity,
      summary,
      summaryFromLlm: !!llm,
      surfacedSignals,
      notCited: !anySiteCited,
      receiptCount: providers.reduce((sum, p) => sum + p.citations.length, 0),
      providers,
    });
  }

  // Order questions by Big-5 display order, then by their position-ish (kept as
  // insertion order within a category).
  questions.sort(
    (a, b) => BIG5_DISPLAY_ORDER.indexOf(a.category) - BIG5_DISPLAY_ORDER.indexOf(b.category),
  );

  // Exposure summary. Derive the surfaced-signal count from the SAME union the
  // grouped pills use (per-question surfaced signals over the questions we
  // actually render), so the meter count, the pills, and the headline number
  // can never disagree. visibility/sov still come from the metrics helper.
  const m = deriveMetricsFromResult(result);
  const surfacedKeys = new Set<number>();
  for (const q of questions) for (const s of q.surfacedSignals) surfacedKeys.add(s.key);
  const signalsExposed = Math.min(surfacedKeys.size, TRUST_SIGNALS_JUDGED);

  const groups: ExposureGroup[] = SIGNAL_GROUP_ORDER.map((group) => {
    const keys = SIGNAL_KEYS_BY_GROUP[group];
    const surfaced = keys
      .filter((k) => surfacedKeys.has(k))
      .map((k) => ({ key: k, name: signalName(k) }));
    return { group, surfaced, absentCount: keys.length - surfaced.length };
  });

  // Big-5 categories present (for the tab bar), in fixed display order.
  const countByCat = new Map<Big5Category, number>();
  for (const q of questions) countByCat.set(q.category, (countByCat.get(q.category) || 0) + 1);
  const categories = BIG5_DISPLAY_ORDER.filter((c) => countByCat.has(c)).map((category) => ({
    category,
    label: BIG5_LABELS[category],
    count: countByCat.get(category) || 0,
  }));

  const severity = overallSeverity(m.visibility, signalsExposed);
  const headline =
    severity === "strong"
      ? `AI consistently points buyers to ${companyName}.`
      : severity === "weak"
        ? `AI rarely points buyers to ${companyName}.`
        : `AI mentions ${companyName}, but other companies are showing up too.`;
  const detail =
    m.visibility == null
      ? "No answers were judged in this run."
      : `${companyName} appeared in ${pct(m.visibility)} of AI answers, with ${signalsExposed} of ${TRUST_SIGNALS_TOTAL} AI Trust Signals surfaced.`;

  // Focus vs the buyer's roster competitors on signal exposure. The judge names
  // many other companies too, but comparing only against the chosen competitors
  // keeps the view on-message and uncluttered.
  const focusDomain =
    (result.brands || []).find((b) => b?.isCustomer)?.domain ||
    (typeof result.company?.url === "string" ? (result.company.url as string) : "") ||
    "";
  const roster: { name: string; url: string }[] = [];
  for (const c of result.competitors || []) {
    const name = (c?.name || "").trim();
    if (name) roster.push({ name, url: c?.url || c?.domain || "" });
  }
  if (roster.length === 0) {
    for (const b of result.brands || []) {
      const name = (b?.name || "").trim();
      if (b && !b.isCustomer && name) roster.push({ name, url: b.domain || "" });
    }
  }
  const focusVisibility = totalJudged > 0 ? focusAppearances / totalJudged : null;
  const competitorExposure: CompanyExposure[] = [
    {
      name: companyName,
      url: focusDomain,
      signalsExposed,
      visibility: focusVisibility,
      isFocus: true,
      appeared: true,
    },
  ];
  for (const c of roster) {
    const lc = c.name.trim().toLowerCase();
    const keys = new Set<number>();
    let apps = 0;
    let appeared = false;
    const tally = (subjName: string) => {
      appeared = true;
      for (const k of competitorSignalKeys.get(subjName) || []) keys.add(k);
      apps += competitorAppearances.get(subjName) || 0;
    };
    // Prefer an exact name match; fall back to a WORD-BOUNDED containment only
    // for names long enough not to false-positive (so "Box" can't absorb
    // "Dropbox", but "Marketo" still groups "Adobe Marketo Engage"). Single
    // direction (roster name found inside the subject) avoids a long roster
    // name swallowing an unrelated short subject.
    if (competitorSignalKeys.has(lc) || competitorAppearances.has(lc)) {
      tally(lc);
    } else if (lc.length >= 4) {
      // Unicode-aware word boundary (ASCII \b would miss accented/non-Latin
      // names like "nestlé waters"): the name must sit between non-alphanumerics
      // or string edges.
      const re = new RegExp(
        `(?:^|[^\\p{L}\\p{N}])${escapeRegExp(lc)}(?:[^\\p{L}\\p{N}]|$)`,
        "u",
      );
      for (const subjName of competitorAppearances.keys()) {
        if (re.test(subjName)) tally(subjName);
      }
    }
    competitorExposure.push({
      name: c.name,
      url: c.url,
      signalsExposed: Math.min(keys.size, TRUST_SIGNALS_JUDGED),
      // Cap at totalJudged so name-variant grouping can't push it over 100%.
      visibility: totalJudged > 0 ? Math.min(apps, totalJudged) / totalJudged : null,
      isFocus: false,
      appeared,
    });
  }
  competitorExposure.sort((a, b) => {
    if (a.isFocus !== b.isFocus) return a.isFocus ? -1 : 1;
    if (a.appeared !== b.appeared) return a.appeared ? -1 : 1;
    return b.signalsExposed - a.signalsExposed;
  });

  // Surfaced signals total uses the union count; absent against the 19 headline.
  const surfacedCount = signalsExposed;
  return {
    companyName,
    overall: { severity, headline, detail, signalsExposed, visibility: m.visibility, sov: m.sov },
    exposure: {
      surfacedCount,
      absentCount: TRUST_SIGNALS_TOTAL - surfacedCount,
      total: TRUST_SIGNALS_TOTAL,
      groups,
    },
    competitorExposure,
    categories,
    questions,
  };
}

export const SEVERITY_LABELS: Record<Severity, string> = {
  strong: "Strong",
  mid: "Mixed",
  weak: "Weak",
};
